import { useEffect, useMemo, useState } from 'react'
import { PageHeader } from '../ui/ui'
import { useI18n } from '../i18n'
import { SECTIONS_FR } from './guide/sections.fr'
import { SECTIONS_EN } from './guide/sections.en'
import type { GuideSection } from './guide/parts'

/**
 * Guide utilisateur, servi depuis l'application elle-même.
 *
 * Le guide vit ici plutôt que dans un PDF joint : un document téléchargé se
 * périme en silence, alors qu'un écran suit les livraisons. Chaque section
 * porte une ancre, ce qui permet d'envoyer à un collègue le lien d'un passage
 * précis (#/guide#traiter-une-detection) plutôt que « page 7 du PDF ».
 *
 * Le contenu est volontairement du JSX et non du Markdown : pas de dépendance
 * supplémentaire à charger pour tous les écrans, et les renvois vers les autres
 * pages de l'outil sont de vrais liens de navigation.
 *
 * La prose a un fichier par langue (`guide/sections.fr.tsx`, `sections.en.tsx`)
 * plutôt que des clés de catalogue : treize sections de texte suivi se relisent
 * mal découpées en centaines de clés, et un relecteur doit pouvoir lire la
 * version anglaise d'un trait. Les ancres, elles, sont communes : un lien
 * envoyé à un collègue reste valable quelle que soit sa langue.
 */
const SECTIONS_BY_LANG: Record<'fr' | 'en', GuideSection[]> = {
  fr: SECTIONS_FR,
  en: SECTIONS_EN,
}

export function GuidePage() {
  const { lang, t } = useI18n()
  const sections = SECTIONS_BY_LANG[lang]
  const [active, setActive] = useState<string>(sections[0].id)
  const ids = useMemo(() => sections.map((s) => s.id), [sections])

  // Le sommaire suit la lecture : la section la plus haute encore visible est
  // celle que l'on est en train de lire.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
        if (visible) setActive(visible.target.id)
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
    )
    for (const id of ids) {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    }
    return () => observer.disconnect()
  }, [ids])

  return (
    <>
      <PageHeader title={t('screen.guide.title')} description={t('guide.desc')} />
      <div className="guide-layout">
        <nav className="guide-toc" aria-label={t('guide.toc')}>
          <p className="guide-toc-title">{t('guide.tocTitle')}</p>
          <ol>
            {sections.map((s) => (
              <li key={s.id}>
                <a
                  href="#/guide"
                  className={s.id === active ? 'guide-toc-link guide-toc-link-active' : 'guide-toc-link'}
                  onClick={(e) => {
                    e.preventDefault()
                    document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  {t(s.titleKey)}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <article className="guide-body">
          {sections.map((s) => (
            <section key={s.id} id={s.id} className="guide-section">
              <h2>{t(s.titleKey)}</h2>
              {s.body}
            </section>
          ))}
        </article>
      </div>
    </>
  )
}
